import { Component } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { LoadingBarComponent } from '../../../../shared/components/loading-bar/loading-bar.component';
import { ButtonTypeEnum } from '../../../../shared/components/button/types/button-type.enum';
import { ButtonSizeEnum } from '../../../../shared/components/button/types/button-size.enum';
import { ButtonRadiusEnum } from '../../../../shared/components/button/types/button-radius.enum';
import { ModalService } from '../../../../shared/services/modal/modal.service';
import { SuccessModalComponent } from '../../../../shared/modals/success-modal/success-modal.component';
import { UserService } from '../../../../shared/services/user2/user.service';
import { ToastService } from '../../../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../../../shared/utils/functions';

const ALLOWED_TYPES: ReadonlySet<string> = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
]);
const ALLOWED_EXTENSIONS: ReadonlySet<string> = new Set(['.xlsx', '.xls', '.csv']);

@Component({
  selector: 'l-import-users-modal',
  standalone: true,
  templateUrl: './import-users-modal.component.html',
  imports: [
    NgClass,
    FormsModule,
    ButtonComponent,
    IconComponent,
    LoadingBarComponent,
  ],
})
export class ImportUsersModalComponent {

  constructor(
    private readonly ref: DynamicDialogRef,
    private readonly modalService: ModalService,
    private readonly userService: UserService,
    private readonly toastService: ToastService,
  ) { }

  public selectedFile: File | null = null;

  public updateExisting: boolean = false;

  public isLoading: boolean = false;

  public fileError: string | null = null;

  public readonly buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum: typeof ButtonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  public get isSubmitEnabled(): boolean {
    return !!this.selectedFile && !this.fileError;
  }

  public close(): void {
    this.ref.close();
  }

  public downloadTemplate(): void {
    const link = document.createElement('a');
    link.href = 'assets/templates/import-user-template.xlsx';
    link.download = 'import-user-template.xlsx';
    link.click();
  }

  public onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
      input.value = '';
    }
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleFile(file);
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  public removeFile(): void {
    this.selectedFile = null;
    this.fileError = null;
  }

  public async submit(): Promise<void> {
    if (!this.selectedFile || this.isLoading) {
      return;
    }

    this.isLoading = true;

    try {
      await this.userService.importUsers(this.selectedFile, this.updateExisting);

      this.ref.close();
      this.modalService.open(SuccessModalComponent, {
        width: '500px',
        modal: true,
        closable: false,
        showHeader: false,
      });
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isLoading = false;
    }
  }

  private handleFile(file: File): void {
    this.fileError = null;

    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = ALLOWED_TYPES.has(file.type) || ALLOWED_EXTENSIONS.has(extension);

    if (!isValidType) {
      this.fileError = 'Formato inválido. Envie um arquivo .xlsx, .xls ou .csv';
      this.selectedFile = null;
      return;
    }

    this.selectedFile = file;
  }

}
